import React, { useEffect, useRef } from 'react';

import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';

import ProtectedNavLink from './ProtectedNavLink';

import MyAccountTitle from './titles/MyAccountTitle';
import AdminTitle from './titles/AdminTitle';
import DevPortalTitle from './titles/DevPortalTitle';
import HomeTitle from './titles/HomeTitle';
import InteractiveIcon from './InteractiveIcon';
import IoTHubTitle from './titles/IoTHubTitle';
import ArchitectTitle from './titles/ArchitectTitle';
import CompanySelect from '../containers/Global/CompanySelect';
import RulesTitle from './titles/RulesTitle';
import ServiceManagementTitle from './titles/ServiceManagementTitle';
import SubscriptionsTitle from './titles/SubscriptionsTitle';

import { applicationTitles } from '../localization/en-us/applicationTitles';

import classes from './Sidebar.module.css';

const Sidebar = ({
    toggleState,
    ...props
}) => {
    const title = () => {
        switch (props.app) {
            case 'myaccount':
                return <MyAccountTitle />;
            case 'admin':
                return <AdminTitle />;
            case 'devportal':
                return <DevPortalTitle />;
            case 'home':
                return <HomeTitle />;
            case 'iothub':
                return <IoTHubTitle />;
            case 'architect':
                return <ArchitectTitle />;
            case 'rules':
                return <RulesTitle />;
            case 'servicemanagement':
                return <ServiceManagementTitle />;
            case 'subscriptions':
                return <SubscriptionsTitle />;
            default:
                break;
        }
    };

    const slideRef = useRef();

    useEffect(() => {
        if (toggleState) {
            slideRef.current = 'show';

            return;
        }

        slideRef.current = 'hide';
    }, [toggleState]);

    return (
        <Drawer
            anchor="left"
            open={toggleState}
            onClose={props.handleClickAway}
        >
            <div className={classes.Sidebar}>
                <InteractiveIcon
                    className={classes.menuBox}
                    icon="material-icons"
                    iconClassName={classes.menuBoxIcon}
                    label="Applications Menu"
                    onClick={props.toggle}
                >
                    apps
                </InteractiveIcon>
                {title()}
                <p className={classes.sidebarTitle}>Applications</p>
                <div className={classes.bodyContainer}>
                    <List>
                        {
                            process.env.bobV2Enabled &&
                            <div className={classes.sidebarItem}>
                                <ProtectedNavLink
                                    className={classes.sidebarCopy}
                                    permissions="homeRead"
                                    isEnabled={props.isEnabled}
                                    aria-label="Dashboard"
                                    exact
                                    imgsrc="/assets/images/dashboardColorIcon.svg"
                                    to={{
                                        pathname: '/v2/dashboard/company',
                                        search:   props.history.location.search
                                    }}
                                >
                                    {applicationTitles.dashboard}
                                </ProtectedNavLink>
                            </div>
                        }
                        {
                            process.env.serviceManagementEnabled &&
                            <div className={classes.sidebarItem}>
                                <ProtectedNavLink
                                    className={classes.sidebarCopy}
                                    onClick={() => props.appToggle('servicemanagement')}
                                    permissions="serviceManagementRead"
                                    isEnabled={props.isEnabled}
                                    aria-label="Service Requests"
                                    exact
                                    imgsrc="/assets/images/serviceManualIconColor.svg"
                                    to={{
                                        pathname: '/servicemanagement',
                                        search:   props.history.location.search
                                    }}
                                >
                                    {applicationTitles.serviceManagement}
                                </ProtectedNavLink>
                            </div>
                        }
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('rules')}
                                permissions="rulesRead"
                                isEnabled={props.isEnabled}
                                aria-label="Rule Builder"
                                exact
                                imgsrc="/assets/images/rulesColorIcon.svg"
                                to={{
                                    pathname: '/rules',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.ruleBuilder}
                            </ProtectedNavLink>
                        </div>
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('architect')}
                                permissions="architectRead"
                                isEnabled={props.isEnabled}
                                aria-label="Architect"
                                exact
                                imgsrc="/assets/images/architectColorIcon.svg"
                                to={{
                                    pathname: '/architect',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.architect}
                            </ProtectedNavLink>
                        </div>
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('iothub')}
                                permissions="iothubRead"
                                isEnabled={props.isEnabled}
                                aria-label="IoT Hub"
                                exact
                                imgsrc="/assets/images/iotHubColorIcon.svg"
                                to={{
                                    pathname: '/iothub',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.iotHub}
                            </ProtectedNavLink>
                        </div>
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('subscriptions')}
                                permissions="default"
                                isEnabled={props.isEnabled}
                                aria-label="Subscriptions"
                                exact
                                imgsrc="/assets/images/subscriptionsIconColor.svg"
                                to={{
                                    pathname: '/subscriptions',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.subscriptions}
                            </ProtectedNavLink>
                        </div>
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('admin')}
                                permissions="adminRead"
                                aria-label="Admin"
                                exact
                                imgsrc="/assets/images/adminColorIcon.svg"
                                to={{
                                    pathname: '/admin',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.admin}
                            </ProtectedNavLink>
                        </div>
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('myaccount')}
                                permissions="default"
                                aria-label="My Account"
                                exact
                                imgsrc="/assets/images/myAccountColorIcon.svg"
                                to={{
                                    pathname: '/myaccount',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.myAccount}
                            </ProtectedNavLink>
                        </div>
                        <div className={classes.sidebarItem}>
                            <ProtectedNavLink
                                className={classes.sidebarCopy}
                                onClick={() => props.appToggle('devPortal')}
                                permissions="default"
                                isEnabled={props.isEnabled}
                                aria-label="API Docs"
                                exact
                                imgsrc="/assets/images/devPortalColorIcon.svg"
                                to={{
                                    pathname: '/devportal',
                                    search:   props.history.location.search
                                }}
                            >
                                {applicationTitles.devPortal}
                            </ProtectedNavLink>
                        </div>
                    </List>
                    {props.companyInfo && props.companies.length > 0 &&
                    <React.Fragment>
                        <p className={classes.sidebarTitle}>Switch Company</p>
                        <List>
                            <div className={`${classes.sidebarItem} ${classes.selectCompany}`}>
                                <img src="/assets/images/appartmentComplexFlat.svg" alt="select company" />
                                <CompanySelect
                                    clearErrors={props.clearErrors}
                                    companyInfo={props.companyInfo}
                                    companies={props.companies}
                                    companySelect={props.companySelect}
                                />
                            </div>
                        </List>
                    </React.Fragment>
                    }
                </div>
            </div>
        </Drawer>
    );
};

export default Sidebar;
